// Pure rules for the launcher label, quick buttons and status copy. No React and no
// imports from ../i18n (a directory import node ESM cannot resolve), so node --test can
// load this file directly. Translated strings are passed in by the caller.

export const LABEL_MIN_VISIBLE_MS = 10000;
/** Narrow screens show fewer quick buttons so the stack does not cover page content. */
export const MOBILE_QUICK_BUTTON_CAP = 3;
export const MAX_QUICK_BUTTONS = 4;

/** The grey placeholder the server sends for an agent with no avatar (app/functions.php avatarUrl). */
const DEFAULT_AVATAR_MARKER = '/assets/images/default-avatar.png';

export function labelStorageKey(appId) {
    return `lane_launcher_label_${appId || 'app'}`;
}

export function quickButtonsDismissKey(appId) {
    return `lane_quick_buttons_dismissed_${appId || 'app'}`;
}

/**
 * Where the label stands, from the sessionStorage record and the clock.
 *
 * It shows once per session. The record survives navigation and refresh, so a reload
 * inside the 10 seconds resumes with the time left, and one after them does not bring
 * the label back.
 *
 * @param {string|null} raw stored value: null, a timestamp, or 'done'
 * @returns {{show: boolean, remainingMs: number}}
 */
export function resolveLabelSession(raw, now) {
    if (raw === 'done') return { show: false, remainingMs: 0 };
    if (raw === null || raw === undefined || raw === '') {
        return { show: true, remainingMs: LABEL_MIN_VISIBLE_MS };
    }
    const shownAt = Number(raw);
    if (!Number.isFinite(shownAt)) return { show: true, remainingMs: LABEL_MIN_VISIBLE_MS };
    const elapsed = now - shownAt;
    if (elapsed >= LABEL_MIN_VISIBLE_MS) return { show: false, remainingMs: 0 };
    return { show: true, remainingMs: LABEL_MIN_VISIBLE_MS - Math.max(0, elapsed) };
}

/**
 * Should the pill be on screen right now. A teaser or message preview occupies the same
 * spot and carries real information, so it always wins.
 */
export function shouldRenderLabel({ enabled, isMobile, mobileEnabled, sessionDone, isChatOpen, teaserVisible, launcherHidden }) {
    if (!enabled || sessionDone || isChatOpen || teaserVisible || launcherHidden) return false;
    if (isMobile && !mobileEnabled) return false;
    return true;
}

export function labelLines({ text, defaultText, showResponseTime, responseTimeText, avgReplyTime }) {
    const primary = (typeof text === 'string' && text.trim()) ? text.trim() : defaultText;
    const secondary = showResponseTime ? ((responseTimeText || avgReplyTime || '').trim() || null) : null;
    return { primary, secondary };
}

export function isSafeHttpUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch (_) {
        return false;
    }
}

/**
 * Filter the delivered buttons again before rendering. The server is the real check, but a
 * cached config should not be able to render a javascript: link or crash the launcher.
 */
export function visibleQuickButtons(list, isMobile) {
    if (!Array.isArray(list)) return [];
    const valid = list.filter((b) => {
        if (!b || typeof b !== 'object' || !b.id || typeof b.text !== 'string' || !b.text.trim()) return false;
        if (b.action === 'link') return isSafeHttpUrl(b.url);
        return b.action === 'message' || b.action === 'flow';
    });
    return valid.slice(0, isMobile ? MOBILE_QUICK_BUTTON_CAP : MAX_QUICK_BUTTONS);
}

/** The button object chat.js handleButtonClick takes, so a flow button routes like any other. */
export function flowButtonPayload(button) {
    return { id: button.id, value: button.id, text: button.text };
}

/** The text a message button sends. */
export function quickMessageText(button) {
    return (typeof button.message === 'string' && button.message.trim()) ? button.message.trim() : button.text;
}

/**
 * Hours as the visitor reads them: "9:00 AM – 6:00 PM GMT+9". The clock is the merchant's,
 * so the zone is always shown. Returns '' when no hours are configured.
 */
export function formatBusinessHours(hours, lang) {
    if (!hours || typeof hours !== 'object') return '';
    const start = Number(hours.start);
    const end = Number(hours.end);
    if (!Number.isInteger(start) || !Number.isInteger(end) || start === end) return '';
    const fmt = (h) => new Intl.DateTimeFormat(lang || undefined, {
        hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
    }).format(new Date(Date.UTC(2024, 0, 1, h % 24, 0)));
    let range;
    try {
        range = `${fmt(start)} – ${fmt(end)}`;
    } catch (_) {
        return '';
    }
    if (!hours.timezone) return range;
    try {
        const tzName = new Intl.DateTimeFormat('en-US', { timeZone: hours.timezone, timeZoneName: 'short' })
            .formatToParts(new Date())
            .find((p) => p.type === 'timeZoneName');
        return tzName ? `${range} ${tzName.value}` : range;
    } catch (_) {
        return range;
    }
}

/**
 * The status line when no agent is online. 'auto' is the chain the widget has always used,
 * so tenants who never touch the option see no change.
 *
 * @returns {string} '' means render no line; callers keep their own fallback rules
 */
export function resolveOfflineStatus(theme, { hoursLine = '' } = {}) {
    const t = theme || {};
    const response = t.responseTimeText || '';
    const average = t.avgReplyTime || '';
    if (t.offlineStatusMode === 'business_hours' && hoursLine) return hoursLine;
    if (t.offlineStatusMode === 'response_time' || t.offlineStatusMode === 'business_hours') {
        return response || average;
    }
    return average || response;
}

/**
 * The header title. The untouched default is translated; a title the merchant set is never
 * altered, including one that happens to read "Customer Service".
 */
export function resolveServiceTitle(appConfig, defaultTitle) {
    const cfg = appConfig || {};
    if (cfg.service_title_is_default || !cfg.service_title) return defaultTitle;
    return cfg.service_title;
}

export function isDefaultAvatar(url) {
    return typeof url === 'string' && url.includes(DEFAULT_AVATAR_MARKER);
}

/**
 * The avatars the Home hero shows: agents with a real photo, else the app logo, else none.
 * The server gives photo-less agents one grey placeholder, so two of them rendered as a
 * pair of identical grey circles that said nothing about the business.
 *
 * @returns {{kind: 'agents', agents: Array} | {kind: 'logo', url: string} | {kind: 'none'}}
 */
export function resolveHomeIdentity(agents, logoUrl) {
    const real = (Array.isArray(agents) ? agents : []).filter((a) => a && a.avatar_url && !isDefaultAvatar(a.avatar_url));
    if (real.length > 0) return { kind: 'agents', agents: real };
    if (typeof logoUrl === 'string' && logoUrl) return { kind: 'logo', url: logoUrl };
    return { kind: 'none' };
}

/**
 * Home block order for the conversation-first layout: the start card leads and help
 * articles sink to the bottom. The merchant's on/off choices still apply.
 */
export function conversationFirstBlocks(blocks) {
    const rank = { conversations: 0, links: 1, search: 2, articles: 3 };
    return [...blocks].sort((a, b) => (rank[a.type] ?? 9) - (rank[b.type] ?? 9));
}

/** Recent conversation row title: the message preview, or wording with no internal id. */
export function conversationRowTitle(lastMessage, fallback, maxLen = 40) {
    const text = typeof lastMessage === 'string' ? lastMessage.trim() : '';
    if (!text) return fallback;
    return text.length > maxLen ? `${text.substring(0, maxLen)}...` : text;
}
